import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { Booking } from '@shared/types';
import { Link } from 'react-router-dom';

export default function Bookings() {
  const { currentUser } = useAuth();

  const { data: bookings, isLoading } = useQuery({
    queryKey: ['bookings', currentUser?.uid],
    queryFn: async () => {
      if (!currentUser) return [];

      const q = query(
        collection(db, 'bookings'),
        where('guestId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          checkinTimestamp: data.checkinTimestamp instanceof Timestamp ? data.checkinTimestamp.toDate() : data.checkinTimestamp,
          checkoutTimestamp: data.checkoutTimestamp instanceof Timestamp ? data.checkoutTimestamp.toDate() : data.checkoutTimestamp,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        };
      }) as Booking[];
    },
    enabled: !!currentUser,
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">My Bookings</h1>
      {bookings && bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking) => {
            const checkIn = booking.checkinTimestamp.toLocaleDateString('en-ZA');
            const checkOut = booking.checkoutTimestamp.toLocaleDateString('en-ZA');
            const total = (booking.totalCents / 100).toFixed(2);

            return (
              <div key={booking.id} className="card">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Booking #{booking.id.slice(0, 8)}</h3>
                    <p className="text-gray-600 mb-1">
                      {checkIn} - {checkOut}
                    </p>
                    <p className="text-gray-600 mb-1">{booking.nights} nights</p>
                    <p className="text-lg font-bold text-[#32373c]">R {total}</p>
                  </div>
                  <div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        booking.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/listing/${booking.listingId}`}
                  className="text-[#32373c] hover:text-[#2a2e33] mt-4 inline-block"
                >
                  View Listing →
                </Link>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg mb-4">No bookings yet</p>
          <Link to="/search" className="btn btn-primary">
            Browse Properties
          </Link>
        </div>
      )}
    </div>
  );
}


